import { useState, useEffect, useMemo } from 'react';

import gql from 'graphql-tag';
import { useDebouncedCallback } from 'use-debounce';

import { useLazyQuery } from '@apollo/client';

import { LaneType } from 'common-types';
import { getClient } from '../apollo';
import { PublicUserFragment } from '../graphql/fragments';
import { BlockSortDirection } from 'constants-content';
import {
  NumericSearchTypeEnum,
  StringSearchTypeEnum,
} from 'constants-activate';

const query = gql`
  ${PublicUserFragment}

  query interactionsOnChannelQuery(
    $channelId: UUID!
    $sectionId: UUID
    $paymentAccountId: UUID
    $merchantAccountId: UUID
    $search: UserContentInteractionSearch
    $pagination: PaginationInput
  ) {
    interactionsOnChannel(
      channelId: $channelId
      sectionId: $sectionId
      paymentAccountId: $paymentAccountId
      merchantAccountId: $merchantAccountId
      search: $search
      pagination: $pagination
    ) {
      pageInfo {
        total
        start
        perPage
      }
      items {
        _id
        _created
        _updated
        _createdBy {
          ...PublicUserFragment
        }
        _updatedBy {
          ...PublicUserFragment
        }
        user {
          _id
          name
          profile {
            _id
            name
            image
            logo
          }
        }
        startDate
        endDate
        geo
        data
        state
        features
        contentData
        content {
          _id
          name
        }
        version
        status
      }
    }
  }
`;

export interface variablesType {
  channelId: LaneType.UUID;
  sectionId: LaneType.UUID;
  merchantAccountId: LaneType.UUID;
  paymentAccountId: LaneType.UUID;
  pagination: {
    start: number;
    perPage: number;
  };
  search: {
    searchValue?: {};
    sortBy: {
      key: string;
      dir: BlockSortDirection;
    };
    state?: {
      isOpen: boolean;
      isActive?: boolean;
    };
    user?: {
      name?: {
        type: StringSearchTypeEnum;
        value: string;
      };
    };
    _created?: {
      type: NumericSearchTypeEnum;
      value: Date;
      valueB: Date;
    };
    startDate?: {
      type: NumericSearchTypeEnum;
      value: Date;
      valueB: Date;
    };
    status?: {
      type: 'eq';
      value: string;
    };
  };
}

const defaultSearch = {};

type Props = {
  channelId: LaneType.UUID;
  paymentAccountId?: LaneType.UUID;
  sectionId?: LaneType.UUID;
  merchantAccountId?: LaneType.UUID;
  selectedSort?: string;
  selectedOrder?: string;
  selectedState?: string;
  selectedStatus?: string;
  perPage?: number;
  search?: any;
  keyword?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  startCreationDate?: Date | null;
  endCreationDate?: Date | null;
};

export default function useInteractionsOnChannelQuery({
  channelId,
  paymentAccountId,
  sectionId,
  merchantAccountId,
  selectedSort = '_updated',
  selectedOrder = 'asc',
  selectedState = 'any',
  selectedStatus = 'any',
  perPage = 100,
  search = defaultSearch,
  keyword,
  startDate,
  endDate,
  startCreationDate,
  endCreationDate,
}: Props) {
  const [page, setPage] = useState(0);
  const [searchValue, setSearchValue] = useState(search);

  const debouncedSearchValue = useDebouncedCallback(function () {
    setSearchValue(search);
  }, 500).callback;

  const [fetchInteractions, { data, loading, error }] = useLazyQuery(query, {
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!loading) {
      debouncedSearchValue();
    }
  }, [JSON.stringify(search)]);

  const interactions = useMemo(() => {
    if (data?.interactionsOnChannel?.items) {
      return data.interactionsOnChannel.items;
    }

    return [];
  }, [data?.interactionsOnChannel?.items]);

  const pageInfo = useMemo(() => {
    if (data?.interactionsOnChannel?.pageInfo) {
      return data.interactionsOnChannel.pageInfo;
    }

    return {
      total: 0,
      start: 0,
      perPage,
    };
  }, [perPage, data?.interactionsOnChannel?.pageInfo]);

  const variables = useMemo(() => {
    const searchObj: any = {
      ...searchValue,
      sortBy: {
        key: selectedSort,
        dir: selectedOrder,
      },
    };

    if (keyword) {
      searchObj.search = {
        type: StringSearchTypeEnum.Like,
        value: keyword,
      };
    }

    if (selectedState === 'open') {
      searchObj.state = {
        isOpen: true,
      };
    } else if (selectedState === 'closed') {
      searchObj.state = {
        isOpen: false,
      };
    }

    if (selectedStatus && selectedStatus !== 'any') {
      searchObj.status = {
        type: 'eq',
        value: selectedStatus,
      };
    }

    const created =
      startCreationDate && endCreationDate
        ? {
            type: NumericSearchTypeEnum.Between,
            value: startCreationDate,
            valueB: endCreationDate,
          }
        : null;

    const reservation =
      startDate && endDate
        ? {
            type: NumericSearchTypeEnum.Between,
            value: startDate,
            valueB: endDate,
          }
        : null;

    if (created) {
      searchObj._created = created;
    }

    if (reservation) {
      searchObj.startDate = reservation;
    }

    return {
      channelId,
      sectionId,
      merchantAccountId,
      paymentAccountId,
      pagination: {
        start: page * perPage,
        perPage,
      },
      search: searchObj,
    };
  }, [
    channelId,
    sectionId,
    paymentAccountId,
    merchantAccountId,
    page,
    perPage,
    selectedOrder,
    selectedSort,
    selectedState,
    selectedStatus,
    searchValue,
    keyword,
    startDate,
    endDate,
    startCreationDate,
    endCreationDate,
  ]);

  function refresh() {
    fetchInteractions({
      variables,
    });
  }

  useEffect(() => {
    if (
      (channelId || sectionId || paymentAccountId || merchantAccountId) &&
      !loading
    ) {
      fetchInteractions({
        variables,
      });
    }
  }, [variables]);

  return {
    interactions,
    pageInfo,
    page,
    perPage,
    loading,
    error,
    setPage,
    refresh,
    variables,
  };
}
